/**
 * Scripts API Schemas
 *
 * Re-exports script-related schemas from @aica/core for API usage.
 * The core domain schemas live in @aica/core, this file provides
 * a unified import point for API types.
 *
 * Endpoints:
 * - GET /api/v1/scripts - List scripts
 * - POST /api/v1/scripts - Create script
 * - GET /api/v1/scripts/:id - Get script
 * - PATCH /api/v1/scripts/:id - Update script
 * - DELETE /api/v1/scripts/:id - Delete script
 * - POST /api/v1/scripts/:id/steps - Create step
 * - PATCH /api/v1/scripts/:id/steps/:stepId - Update step
 * - DELETE /api/v1/scripts/:id/steps/:stepId - Delete step
 * - POST /api/v1/scripts/:id/steps/:stepId/phrases - Create phrase
 * - PATCH /api/v1/scripts/:id/steps/:stepId/phrases/:phraseId - Update phrase
 * - DELETE /api/v1/scripts/:id/steps/:stepId/phrases/:phraseId - Delete phrase
 * - PUT /api/v1/scripts/:id/steps/reorder - Reorder steps
 * - POST /api/v1/projects/:projectId/scripts - Assign script to project
 */

import { z } from 'zod/v4';
import { analyzedCallCategorySchema } from './records';
import { projectStatusSchema } from './projects';
import { searchTruncatedToSchema } from './common';
import { CreateScriptRequestSchema, UpdateScriptRequestSchema } from '@aica/core/schemas';

// Re-export schemas from @aica/core
export {
  // Enums
  ScriptCategorySchema,
  PhraseTypeSchema,
  MatchTypeSchema,
  SpeakerSchema,
  // Domain schemas
  ScriptSchema,
  ScriptStepSchema,
  ScriptStepPhraseSchema,
  ScriptStepWithPhrasesSchema,
  ScriptWithStepsSchema,
  ProjectScriptSchema,
  // Request schemas
  CreateScriptRequestSchema,
  UpdateScriptRequestSchema,
  CreateStepRequestSchema,
  UpdateStepRequestSchema,
  CreatePhraseRequestSchema,
  UpdatePhraseRequestSchema,
  ReorderStepsRequestSchema,
  AssignScriptRequestSchema,
  // Filter schemas
  ScriptFilterSchema,
  ScriptAdherenceFilterSchema,
  // Result schemas
  ScriptAdherenceResultSchema,
  ScriptAdherenceEvaluationSchema,
  StepEvaluationResultSchema,
  MatchedPhraseSchema,
  MissedPhraseSchema,
  OutOfOrderStepSchema,
  // Types
  type ScriptCategoryInferred as ScriptCategory,
  type PhraseTypeInferred as PhraseType,
  type MatchTypeInferred as MatchType,
  type SpeakerInferred as Speaker,
} from '@aica/core/schemas';

// ============================================================================
// API-specific Enums
// ============================================================================

/**
 * Script binding - auto-selection key for the analyze pipeline (migration 059).
 *
 * Mirrors profileBindingSchema (prompt-profiles.ts): a bound script is picked
 * automatically per record by the LLM-detected call category (categorize-step).
 * Chats are categorized the same way: the channel drives the form of the
 * assessment, not the choice of script.
 *
 * Same schema, for the same reason — `analyzedCallCategorySchema` IS the set
 * the categorize-step emits, while `.exclude(['missed', 'short'])` only names
 * the derived statuses that existed when it was written. See the note on
 * `profileBindingSchema`.
 *
 * At most one script per binding per org (partial unique index in D1).
 * NULL/absent = unbound script, assignable via project_scripts as before.
 */
export const scriptBindingSchema = analyzedCallCategorySchema;

export const SCRIPT_BINDINGS = scriptBindingSchema.options;

// ============================================================================
// Request Schemas Extended With `binding` (migration 059)
// ============================================================================

/**
 * `binding` is not part of the @aica/core base request schemas
 * (CreateScriptRequestSchema/UpdateScriptRequestSchema deliberately leave the
 * enum out of the core domain schema). These compose it on top rather than
 * redefining the enum, keeping scriptBindingSchema the single source of
 * truth (same enum used by the analogous prompt-profiles binding field).
 *
 * binding is mutable: null unbinds, a value rebinds (UNIQUE conflict -> 409).
 */
export const createScriptRequestWithBindingSchema = CreateScriptRequestSchema.extend({
  binding: scriptBindingSchema.nullable().optional(),
});

export const updateScriptRequestWithBindingSchema = UpdateScriptRequestSchema.extend({
  binding: scriptBindingSchema.nullable().optional(),
});

// ============================================================================
// API-specific Path Param Schemas
// ============================================================================

/**
 * Path parameter for script ID - used in /api/v1/scripts/:id routes
 */
export const scriptIdParamSchema = z.object({
  id: z.string().uuid('Invalid script ID'),
});

/**
 * Path parameter for step ID - used in /api/v1/scripts/:id/steps/:stepId routes
 */
export const stepIdParamSchema = z.object({
  stepId: z.string().uuid('Invalid step ID'),
});

/**
 * Path parameter for phrase ID - used in /api/v1/scripts/:id/steps/:stepId/phrases/:phraseId routes
 */
export const phraseIdParamSchema = z.object({
  phraseId: z.string().uuid('Invalid phrase ID'),
});

/**
 * Path parameter for project ID - used in /api/v1/projects/:projectId/scripts routes
 */
export const projectIdParamSchema = z.object({
  projectId: z.string().uuid('Invalid project ID'),
});

// ============================================================================
// API-specific Query Schemas
// ============================================================================

/**
 * Query parameters for listing scripts - GET /api/v1/scripts
 *
 * Supports filtering by category, active status, and text search.
 */
export const listScriptsQuerySchema = z.object({
  /**
   * Script category filter
   * - `sales_call`: Sales call scripts
   * - `support_call`: Support call scripts
   * - `sales_chat`: Sales chat scripts
   * - `complaint`: Complaint handling scripts
   * - `custom`: Custom user-defined scripts
   */
  category: z
    .enum(['sales_call', 'support_call', 'sales_chat', 'complaint', 'custom'])
    .optional(),
  /** Filter by active status */
  isActive: z.coerce.boolean().optional(),
  /** Text search in script name/description */
  search: z.string().max(255).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

// ============================================================================
// Domain Schemas (for response data structure)
// ============================================================================

/**
 * Script step phrase - a specific phrase/pattern to detect
 *
 * Phrases are matched against transcript segments to evaluate script adherence.
 * Supports exact matching, semantic similarity, and regex patterns.
 */
export const scriptStepPhraseSchema = z.object({
  id: z.string(),
  stepId: z.string(),
  /** The phrase text to match (e.g., "Thank you for calling") */
  phraseText: z.string(),
  /**
   * Phrase type determines scoring impact:
   * - `required`: Must be present (negative score if missing)
   * - `optional`: Bonus points if present
   * - `forbidden`: Penalty if present (e.g., profanity)
   */
  phraseType: z.enum(['required', 'optional', 'forbidden']),
  /**
   * Match type strategy:
   * - `exact`: String exact match (case-insensitive)
   * - `semantic`: AI semantic similarity (uses embeddings)
   * - `regex`: Regular expression pattern
   */
  matchType: z.enum(['exact', 'semantic', 'regex']),
  /** Threshold for semantic matching (0-1, typically 0.7-0.9) */
  semanticThreshold: z.number().nullable().optional(),
  /** Weight for scoring calculation (10–200, 100 = normal) */
  weight: z.number(),
  createdAt: z.string(),
});

/**
 * Script step - a sequential stage in the script
 *
 * Steps represent stages like "Greeting", "Discovery", "Pitch", "Close".
 * Each step contains multiple phrases to detect.
 */
export const scriptStepSchema = z.object({
  id: z.string(),
  scriptId: z.string(),
  /** Order of step in script (1-based) */
  stepNumber: z.number(),
  /** Step name (e.g., "Greeting", "Discovery Questions") */
  name: z.string(),
  /** Optional detailed description */
  description: z.string().nullable().optional(),
  /** Whether this step is required for script adherence */
  isRequired: z.boolean(),
  /** Whether steps must occur in order (affects scoring if out of sequence) */
  orderEnforced: z.boolean(),
  /** Weight for scoring calculation (10–200, 100 = normal) */
  weight: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/**
 * Script step with nested phrases
 */
export const scriptStepWithPhrasesSchema = scriptStepSchema.extend({
  phrases: z.array(scriptStepPhraseSchema),
});

/**
 * One project a script is assigned to.
 *
 * `isDefault` — the project's default script (`project_scripts.is_default`),
 * and the ONLY kind of row the pipeline ever resolves. A non-default row is
 * what a project leaves behind when it switches scripts, so it is inert: it
 * must not be read as "assigned".
 * `pinned` — @deprecated, always `false` since #707. The script pin decided no
 * outcome (a project's script outranks the category binding either way, #702)
 * and its control is gone, but the field stays in the response: prod deploys
 * edge-api BEFORE ui-worker, and an already-loaded client validates every
 * `GET /scripts` against this schema — dropping the key would fail that
 * validation and break the Scripts library for every org that has an
 * assignment. Remove once old clients have drained.
 * `projectStatus` — only an ACTIVE project accepts records, so an assignment
 * to a paused or archived one cannot make the script run either. Typed by the
 * project-status enum rather than a bare string: that is what makes
 * "everything except archived" fail to compile instead of silently treating
 * a paused project as working.
 */
export const scriptProjectAssignmentSchema = z.object({
  projectId: z.string(),
  projectName: z.string(),
  projectStatus: projectStatusSchema,
  isDefault: z.boolean(),
  pinned: z.boolean(),
});

/**
 * Base script schema without steps
 *
 * Used for list endpoints where nested steps would be too verbose.
 */
export const scriptSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  /** Script name (e.g., "Sales Discovery Call v2") */
  name: z.string(),
  /** Optional description */
  description: z.string().nullable().optional(),
  /** Script category (sales_call, support_call, etc.) */
  category: z.string(),
  /** Auto-selection key (migration 059): one of 10 call categories; null = unbound. */
  binding: scriptBindingSchema.nullable(),
  /** Whether script is active for evaluation */
  isActive: z.boolean(),
  /** Version number for script revisions */
  version: z.number(),
  /** User who created the script */
  createdByUserId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Number of steps in the script (list endpoints). Optional for backward compatibility. */
  stepCount: z.number().int().nonnegative().optional(),
  /**
   * Projects this script is assigned to (list endpoints).
   *
   * An EMPTY array is meaningful: together with `binding: null` it means the
   * pipeline will never select this script (the cascade in
   * `workers/analyze/script-adherence.ts` ends at "nothing matched"). The
   * field is optional only because detail endpoints do not fill it — treat
   * `undefined` as "not loaded", not as "no assignments".
   */
  projectAssignments: z.array(scriptProjectAssignmentSchema).optional(),
});

/**
 * Script with full nested steps and phrases
 *
 * Used for detail endpoints where full script structure is needed.
 */
export const scriptWithStepsSchema = scriptSchema.extend({
  steps: z.array(scriptStepWithPhrasesSchema),
});

// ============================================================================
// Response Schemas (match actual API responses with `data` wrapper)
// ============================================================================

/**
 * List scripts response - matches GET /api/v1/scripts
 *
 * Returns array of scripts without nested steps (use GET /:id for full script).
 */
export const scriptsListResponseSchema = z.object({
  data: z.array(scriptSchema),
  meta: z
    .object({
      /** Total count of scripts matching filters (for pagination) */
      total: z.number().int(),
    })
    .optional(),
  /** Запрос пришлось укоротить под предел D1 — см. `searchTruncatedToSchema`. */
  searchTruncatedTo: searchTruncatedToSchema,
});

/**
 * Get script with steps response - matches GET /api/v1/scripts/:id
 *
 * Returns full script structure including steps and phrases.
 */
export const scriptWithStepsResponseSchema = z.object({
  data: scriptWithStepsSchema,
});

/**
 * Script CRUD response - matches POST/PATCH /api/v1/scripts
 *
 * Returns created/updated script without nested steps.
 */
export const scriptResponseSchema = z.object({
  data: scriptSchema,
});

/**
 * Step CRUD response - matches POST/PATCH /api/v1/scripts/:id/steps
 *
 * Returns created/updated step without nested phrases.
 */
export const scriptStepResponseSchema = z.object({
  data: scriptStepSchema,
});

/**
 * Step create response - matches POST /api/v1/scripts/:id/steps
 *
 * The created step with its phrases: the request may carry them, and the
 * route answers with the step read back together with them.
 */
export const scriptStepCreatedResponseSchema = z.object({
  data: scriptStepWithPhrasesSchema,
});

/**
 * Phrase CRUD response - matches POST/PATCH /api/v1/scripts/:id/steps/:stepId/phrases
 *
 * Returns created/updated phrase.
 */
export const scriptPhraseResponseSchema = z.object({
  data: scriptStepPhraseSchema,
});

/**
 * Success response - matches DELETE and PUT /api/v1/scripts/:id/steps/reorder
 *
 * Generic success indicator for operations that don't return data.
 */
export const successResponseSchema = z.object({
  success: z.boolean(),
});

// ============================================================================
// Legacy Aliases (backward compatibility)
// ============================================================================

/**
 * @deprecated Use scriptsListResponseSchema instead
 */
export const listScriptsResponseSchema = scriptsListResponseSchema;

/**
 * @deprecated Use scriptResponseSchema instead
 */
export const createScriptResponseSchema = scriptResponseSchema;

// ============================================================================
// Type exports
// ============================================================================

export type ScriptBinding = z.infer<typeof scriptBindingSchema>;
export type CreateScriptRequestWithBinding = z.infer<
  typeof createScriptRequestWithBindingSchema
>;
export type UpdateScriptRequestWithBinding = z.infer<
  typeof updateScriptRequestWithBindingSchema
>;
export type ListScriptsQuery = z.infer<typeof listScriptsQuerySchema>;
export type ScriptResponse = z.infer<typeof scriptResponseSchema>;
export type Script = z.infer<typeof scriptSchema>;
export type ScriptStep = z.infer<typeof scriptStepSchema>;
export type ScriptStepPhrase = z.infer<typeof scriptStepPhraseSchema>;
export type ScriptStepWithPhrases = z.infer<typeof scriptStepWithPhrasesSchema>;
export type ScriptWithSteps = z.infer<typeof scriptWithStepsSchema>;
export type ScriptsListResponse = z.infer<typeof scriptsListResponseSchema>;
export type ScriptWithStepsResponse = z.infer<typeof scriptWithStepsResponseSchema>;
export type ScriptStepResponse = z.infer<typeof scriptStepResponseSchema>;
export type ScriptStepCreatedResponse = z.infer<typeof scriptStepCreatedResponseSchema>;
export type ScriptPhraseResponse = z.infer<typeof scriptPhraseResponseSchema>;
export type SuccessResponse = z.infer<typeof successResponseSchema>;
